'use strict';

export const VIEWPORT_HEIGHT_VAR = '--visual-viewport-height';
export const VIEWPORT_OFFSET_TOP_VAR = '--visual-viewport-offset-top';

function isFocusableFormControl(target) {
  if (!target || typeof target !== 'object') return false;
  const tag = typeof target.tagName === 'string' ? target.tagName.toUpperCase() : '';
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return Boolean(target.isContentEditable);
}

function applyVisualViewportVars(visualViewport, root) {
  if (!visualViewport || !root) return;
  root.style.setProperty(VIEWPORT_HEIGHT_VAR, `${visualViewport.height}px`);
  root.style.setProperty(VIEWPORT_OFFSET_TOP_VAR, `${visualViewport.offsetTop}px`);
}

function clearVisualViewportVars(root) {
  if (!root) return;
  root.style.removeProperty(VIEWPORT_HEIGHT_VAR);
  root.style.removeProperty(VIEWPORT_OFFSET_TOP_VAR);
}

export function isKeyboardTargetIn(container, target) {
  return Boolean(
    container && isFocusableFormControl(target) && container.contains(target),
  );
}

/**
 * Attach visualViewport listeners only while a form field in `getContainer()` is focused.
 */
export function createVisualViewportKeyboardTracker({
  getContainer,
  visualViewport,
  document: doc,
  requestFrame = (cb) => setTimeout(cb, 0),
}) {
  const root = doc.documentElement;
  let listening = false;

  const onViewportChange = () => {
    const container = getContainer();
    const active = doc.activeElement;
    if (!isKeyboardTargetIn(container, active)) {
      return;
    }
    applyVisualViewportVars(visualViewport, root);
    requestFrame(() => {
      if (typeof active.scrollIntoView === 'function') {
        active.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      }
    });
  };

  const attachViewportListeners = () => {
    if (listening || !visualViewport) return;
    listening = true;
    visualViewport.addEventListener('resize', onViewportChange);
    visualViewport.addEventListener('scroll', onViewportChange);
  };

  const detachViewportListeners = () => {
    if (!listening || !visualViewport) return;
    listening = false;
    visualViewport.removeEventListener('resize', onViewportChange);
    visualViewport.removeEventListener('scroll', onViewportChange);
    clearVisualViewportVars(root);
  };

  const onFocusIn = (event) => {
    const container = getContainer();
    const target = event.target;
    if (!isKeyboardTargetIn(container, target)) {
      return;
    }
    applyVisualViewportVars(visualViewport, root);
    attachViewportListeners();
    requestFrame(() => {
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      }
    });
  };

  const onFocusOut = () => {
    requestFrame(() => {
      if (!isKeyboardTargetIn(getContainer(), doc.activeElement)) {
        detachViewportListeners();
      }
    });
  };

  doc.addEventListener('focusin', onFocusIn);
  doc.addEventListener('focusout', onFocusOut);

  return {
    dispose() {
      doc.removeEventListener('focusin', onFocusIn);
      doc.removeEventListener('focusout', onFocusOut);
      detachViewportListeners();
    },
    isListening() {
      return listening;
    },
  };
}
